// Click a parcel → one panel (owner, 2026-10-10): the problem in one plain
// line, every AMEEX event with its time, the route on a small map, the
// courier and the client, the COD and whether it can have reached us.
// Read live from AMEEX each time it opens (GET /ameex/parcel/:code).
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Phone, MessageCircle, Package, Truck, User, X, ArrowLeft, Trash2 } from 'lucide-react';
import { ameexApi, type AmeexBoardParcel } from '@/lib/api';
import { adminOrderHref } from '@/lib/orderHref';
import { formatPhone } from '@/lib/utils';
import { useT } from '@/lib/i18n';
import { problemLine, routeOf, cityKey, lastScanCity, canDeleteParcel } from './board';
import { RouteMap } from './RouteMap';

/** « 0606148406 » → « 212606148406 » for wa.me / tel. */
const intl = (p: string | null | undefined) => {
  const d = String(p ?? '').replace(/\D/g, '');
  if (!d) return null;
  if (d.startsWith('212')) return d;
  if (d.startsWith('0')) return `212${d.slice(1)}`;
  return d.length === 9 ? `212${d}` : d;
};

export function ParcelPanel({ parcel, now, full, onClose }: {
  parcel: AmeexBoardParcel; now: number; full: boolean; onClose: () => void;
}) {
  const { t, lang } = useT();
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const boxRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (full) closeRef.current?.focus();
    // Inline under the lanes on a desktop: bring it into view, the lanes stay put.
    else boxRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, full]);

  const { data: d, isLoading, isError } = useQuery({
    queryKey: ['ameex-dashboard', 'parcel', parcel.code],
    queryFn: () => ameexApi.parcel(parcel.code),
    refetchInterval: 60_000,
  });

  // Delete a parcel AMEEX never picked up (owner, 2026-10-31 — a red error
  // chip he could not get rid of). Two taps: the first asks, the second deletes.
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const del = useMutation({
    mutationFn: (localOnly: boolean) => ameexApi.cancelParcel(parcel.shopifyOrderId, localOnly),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ameex-dashboard'] }); onClose(); },
  });
  const deletable = canDeleteParcel(parcel);

  const fmt = (iso: string | null) => {
    if (!iso) return '';
    const x = new Date(iso);
    const loc = lang === 'en' ? 'en-GB' : 'fr-FR';
    return `${x.toLocaleDateString(loc, { timeZone: 'Africa/Casablanca', day: '2-digit', month: '2-digit' })} ${x.toLocaleTimeString(loc, { timeZone: 'Africa/Casablanca', hour: '2-digit', minute: '2-digit' })}`;
  };
  const day = (iso: string) => new Date(iso).toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-FR', { timeZone: 'Africa/Casablanca', day: '2-digit', month: '2-digit' });

  const line = problemLine(parcel, d ?? null, t, lang, now);
  // AMEEX's events and what the agent did, in one time order.
  const timeline = [
    ...(d?.events ?? []).map((e) => ({ ...e, agent: false })),
    ...(d?.agentAt ? [{ at: d.agentAt, statut: 'AGENT', name: '', comment: null, city: null, hub: null, problem: false, agent: true }] : []),
  ].sort((a, b) => (Date.parse(String(a.at ?? '')) || 0) - (Date.parse(String(b.at ?? '')) || 0));
  const delivered = parcel.status === 'delivered' || Boolean(d?.deliveredAt);
  const tone = parcel.problem ? 'problem' : delivered ? 'done' : 'moving';
  const moving = !parcel.problem && (parcel.lane === 'route' || parcel.lane === 'livraison');
  const dest = cityKey(d?.city ?? parcel.city);
  const route = routeOf(d ?? null);
  const orderHref = adminOrderHref({ shopifyOrderId: parcel.shopifyOrderId });
  const courierTel = intl(d?.courier?.phone);
  const clientWa = intl(d?.client.phone);
  const cod = d?.cod ?? parcel.cod;

  // Whether the money can have reached us: AMEEX never says which parcel it
  // paid, so the panel states the dates, never a guess.
  const money = cod == null ? null
    : cod === 0 ? t('Rien à encaisser — déjà payé.')
      : !delivered ? t('Le livreur l’encaisse à la livraison.')
        : !d?.lastPayoutAt ? t('Encaissé par AMEEX — aucun versement enregistré.')
          : Date.parse(String(d.deliveredAt)) <= Date.parse(d.lastPayoutAt)
            ? t('Livré avant ton dernier versement ({d}).', { d: day(d.lastPayoutAt) })
            : t('Livré après ton dernier versement ({d}) — pas encore versé.', { d: day(d.lastPayoutAt) });

  const btn = 'inline-flex items-center justify-center gap-2 rounded-lg border px-3 min-h-[44px] text-[13px] font-medium cursor-pointer transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500';

  const panel = (
    <section
      ref={boxRef}
      data-adm-root={full ? '' : undefined}
      className={full ? 'fixed inset-0 z-[60] bg-bg-primary overflow-y-auto pb-[env(safe-area-inset-bottom)]' : 'rounded-2xl border border-border-subtle bg-bg-card'}
      role={full ? 'dialog' : 'region'} aria-modal={full || undefined} aria-label={t('Colis {code}', { code: parcel.code })}
    >
      <div className={`flex items-start gap-3 px-5 pt-4 ${full ? 'pb-3 border-b border-border-subtle sticky top-0 bg-bg-primary' : 'pb-2'}`}>
        {full && (
          <button ref={closeRef} type="button" onClick={onClose} aria-label={t('Retour')}
            className="h-11 w-11 shrink-0 -ml-2 rounded-full grid place-items-center text-text-secondary cursor-pointer hover:bg-bg-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
            <ArrowLeft className="h-5 w-5" aria-hidden />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-heading text-[20px] leading-tight text-text-primary truncate">
            {parcel.name ?? t('Sans nom')}
            <span className="text-text-tertiary text-[15px] font-sans"> {parcel.orderName} · {parcel.city ?? '—'}</span>
          </p>
        </div>
        {cod != null && <p className="shrink-0 text-[15px] font-semibold text-text-primary tabular-nums">{t('COD {n} Dh', { n: cod })}</p>}
        {!full && (
          <button type="button" onClick={onClose} aria-label={t('Fermer')}
            className="h-9 w-9 shrink-0 rounded-full grid place-items-center text-text-tertiary cursor-pointer hover:bg-bg-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>

      {line && (
        <p className="mx-5 mb-2 rounded-xl border border-status-stuck-border bg-status-stuck-bg px-4 py-3 text-[14px] font-medium text-status-stuck" aria-live="polite">{line}</p>
      )}
      {isError && <p className="mx-5 mb-2 text-[13px] text-status-waiting">{t('AMEEX ne répond pas pour ce colis. Réessaie dans une minute.')}</p>}

      <div className="grid gap-5 px-5 pb-5 pt-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        <div className="min-w-0">
          {isLoading ? <div className="h-[220px] rounded-xl bg-bg-elevated animate-pulse" aria-hidden />
            : route.length ? <RouteMap route={route} destination={dest} at={lastScanCity(d ?? null)} tone={tone} moving={moving} delivered={delivered} />
              : <p className="text-[12.5px] text-text-tertiary py-6">{t('Ville inconnue — pas de carte.')}</p>}
          <p className="mt-1 text-[11.5px] text-text-tertiary">{t('AMEEX donne la ville de chaque passage, pas la position du livreur.')}</p>
        </div>

        <div className="min-w-0 space-y-4">
          <ol className="relative border-l border-border-subtle ml-1.5" aria-label={t('Historique AMEEX')}>
            {isLoading && <li className="pl-4 py-2 text-[12.5px] text-text-tertiary">{t('Chargement…')}</li>}
            {d && !d.events.length && <li className="pl-4 py-2 text-[12.5px] text-text-tertiary">{t('AMEEX n’a encore rien enregistré.')}</li>}
            {timeline.map((e, i) => (
              <li key={`${e.at}-${i}`} className="relative pl-4 py-1.5">
                <span aria-hidden className={`absolute -left-[5px] top-[11px] h-2.5 w-2.5 rounded-full ${e.agent ? 'bg-info' : e.problem ? 'bg-status-stuck-solid' : e.statut === 'DELIVERED' ? 'bg-status-done-solid' : 'bg-border-strong'}`} />
                <span className="text-[12.5px] text-text-tertiary tabular-nums mr-2">{fmt(e.at)}</span>
                <span className={`text-[14px] ${e.problem ? 'text-status-stuck font-medium' : 'text-text-primary'}`}>{e.agent ? t('Agent : message WhatsApp envoyé') : t(e.name)}</span>
                {(e.hub || e.city) && <span className="text-[12.5px] text-text-tertiary"> · {(e.hub ?? e.city ?? '').replace(/\s*Hub Principal$/i, '')}</span>}
                {e.comment && !/T[ée]l[ée]phone/i.test(e.comment) && <span className="block text-[12.5px] text-text-secondary">{e.comment}</span>}
              </li>
            ))}
          </ol>

          <dl className="space-y-1.5 text-[14px]">
            <div className="flex items-center gap-2">
              <Truck className="h-4 w-4 text-text-tertiary shrink-0" aria-hidden />
              <dt className="text-text-secondary">{t('Livreur')} :</dt>
              <dd className="text-text-primary truncate">{d?.courier ? [d.courier.name, d.courier.phone ? formatPhone(d.courier.phone) : null].filter(Boolean).join(' · ') : t('pas encore attribué')}</dd>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-text-tertiary shrink-0" aria-hidden />
              <dt className="text-text-secondary">{t('Client')} :</dt>
              <dd className="text-text-primary truncate">{d?.client.phone ? formatPhone(d.client.phone) : '—'}</dd>
            </div>
            {money && (
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-text-tertiary shrink-0" aria-hidden />
                <dd className={`${delivered && cod ? 'text-status-done' : 'text-text-primary'}`}>{money}</dd>
              </div>
            )}
          </dl>

          <div className="flex flex-wrap gap-2">
            {courierTel && (
              <a href={`tel:+${courierTel}`} className={`${btn} border-border-subtle text-text-primary hover:bg-bg-elevated`}>
                <Phone className="h-4 w-4" aria-hidden />{t('Appeler le livreur')}
              </a>
            )}
            {clientWa && (
              <a href={`https://wa.me/${clientWa}`} target="_blank" rel="noopener noreferrer" className={`${btn} border-border-subtle text-text-primary hover:bg-bg-elevated`}>
                <MessageCircle className="h-4 w-4" aria-hidden />{t('WhatsApp client')}
              </a>
            )}
            {orderHref && (
              <Link to={orderHref} className={`${btn} border-border-subtle text-text-primary hover:bg-bg-elevated`}>
                <Package className="h-4 w-4" aria-hidden />{t('Ouvrir la commande')}
              </Link>
            )}
          </div>

          {deletable && (
            <div className="border-t border-border-subtle pt-3">
              {!confirming ? (
                <button type="button" onClick={() => { del.reset(); setConfirming(true); }}
                  className={`${btn} border-border-subtle text-text-secondary hover:bg-bg-elevated`}>
                  <Trash2 className="h-4 w-4" aria-hidden />{t('Supprimer ce colis (chez AMEEX aussi)')}
                </button>
              ) : (
                <div className="space-y-2">
                  <p className="text-[13.5px] text-text-primary">{t('Supprimer le colis {code} ? Il sera supprimé chez AMEEX aussi. La commande reste.', { code: parcel.code })}</p>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => del.mutate(false)} disabled={del.isPending}
                      className={`${btn} border-status-stuck-border bg-status-stuck-bg text-status-stuck disabled:opacity-60`}>
                      <Trash2 className="h-4 w-4" aria-hidden />{del.isPending ? t('Suppression…') : t('Oui, supprimer')}
                    </button>
                    <button type="button" onClick={() => setConfirming(false)} disabled={del.isPending}
                      className={`${btn} border-border-subtle text-text-primary hover:bg-bg-elevated`}>
                      {t('Annuler')}
                    </button>
                  </div>
                  {del.isError && (
                    <div className="space-y-2" role="alert">
                      <p className="text-[13px] text-status-stuck">{t('Suppression impossible : {message}', { message: (del.error as Error).message })}</p>
                      {(del.error as { body?: { refused?: boolean } }).body?.refused && (
                        <>
                          <p className="text-[13px] text-text-secondary">{t('AMEEX garde le colis. Tu peux le retirer seulement de cette page — supprime-le aussi dans ton espace AMEEX.')}</p>
                          <button type="button" onClick={() => del.mutate(true)} disabled={del.isPending}
                            className={`${btn} border-border-subtle text-text-primary hover:bg-bg-elevated disabled:opacity-60`}>
                            {t('Retirer de la page seulement')}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
  // Full screen on a phone: at the top of the page, above the tab bar (z 40) —
  // inside the layout's own layer it would sit under it.
  return full && typeof document !== 'undefined' ? createPortal(panel, document.body) : panel;
}
